import { Field, Form, Formik } from 'formik';
import { useState } from 'react';

import styled from 'styled-components';
import * as Yup from 'yup';
import { useAppInit } from '../../../contexts/appInitContext';
import { useOrderContext } from '../../../contexts/orderContext';
import { useTranslation } from '../../../contexts/translationContext';

import { EASYCOM_ROUTES } from '../../../navigation';
import Spacing from '../../atoms/Spacing';
import Typography from '../../atoms/Typography';
import PrimaryButton from '../../molecules/PrimaryButton';
import TextInputComponent from './FormikInput';
import { useAppNavigate } from '../../../hooks/useAppNavigate';

const StyledForm = styled(Form)`
  width: 100%;
`;

interface Ivalues {
  orderId: string;
  customerNumber: string;
}

const SignInCustomerForm = () => {
  const [loading, setLoading] = useState(false);
  const { getOrder, setDefaultOrder } = useOrderContext();
  const navigate = useAppNavigate();
  const { translate } = useTranslation();
  const { updateDefaultParams, params } = useAppInit();
  const { customerNumber, initOrderKeys } = params;
  const [formError, setFormError] = useState(undefined);

  const validationSchema = Yup.object({
    orderId: Yup.string()
      .min(3, (value) => translate('x_characters_long', { min: value.min }))
      .required(
        `${translate('order_no', null)} ${translate(
          'is_required',
          null,
        ).toLowerCase()}`,
      ),
    customerNumber: Yup.string()
      .min(3, (value) => translate('x_characters_long', { min: value.min }))
      .required(
        `${translate('customer_id', null)} ${translate(
          'is_required',
          null,
        ).toLowerCase()}`,
      ),
  });

  const initialValues = {
    orderId: (initOrderKeys && initOrderKeys[0]) || '',
    customerNumber: customerNumber || '',
  };

  const onSubmit = async (values: Ivalues) => {
    setLoading(true);
    setFormError(undefined);
    const { orderId } = values;

    const response = await getOrder({
      orderNumbers: [orderId],
      customerNumber: values.customerNumber,
      email: undefined,
    });

    if (response && response?.errors) {
      setDefaultOrder(orderId);
      setLoading(false);
      setFormError(response.errors[0].message);
    } else {
      updateDefaultParams({ ...params, customerNumber: values.customerNumber });
      setDefaultOrder(response[0].orderNumber);
      setLoading(false);
      navigate(EASYCOM_ROUTES.ORDER_PAGE);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values: Ivalues) => {
        onSubmit(values);
      }}
    >
      <StyledForm>
        <Typography
          align="center"
          translateKey="login_to_see_order"
          variant="h3"
        />
        <Spacing spacing={3} />
        <Field
          placeholder="placeholder_order_id"
          name="orderId"
          type="text"
          translateKey="order_no"
          component={TextInputComponent}
        />
        <Spacing spacing={4} />
        <Field
          placeholder="placeholder_customer_id"
          name="customerNumber"
          type="text"
          translateKey="customer_id"
          component={TextInputComponent}
        />
        <Spacing spacing={4} />
        <PrimaryButton
          icon="arrow-right"
          disabled={loading}
          loading={loading}
          translateKey="sign_in"
          type="submit"
        />
        <Spacing spacing={4} />
        {formError && (
          <Typography variant="input-error">{formError}</Typography>
        )}
      </StyledForm>
    </Formik>
  );
};

export default SignInCustomerForm;
